import React, {useEffect, useRef, useState} from 'react';
import {Formik} from 'formik';
import * as Yup from 'yup';
import {upsertEventTemplate} from 'api/EventTemplatesApi';
import {reportTemplateParameters} from 'constants/dataConstants';
import {useAppContext} from 'contexts/AppContext';
import {isSystemAdmin} from 'utils/Auth';
import {isEmpty} from 'utils/Utils';
import EventTemplateForm from './EventTemplateForm';

const DEFAULT_INSTANCE = {
    id: '',
    name: '',
    alert_type: '',
    alert_text: '',
    params: [],
};
const VALIDATION_SCHEMA = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    alert_type: Yup.string().required('Alert type is required'),
    alert_text: Yup.string().required('Alert text is required'),
    params: Yup.array().of(Yup.string()).min(1, 'At least one parameter is required'),
});

export default function EventTemplateDetails({reportTemplate, maintenanceItemTemplate, eventTemplate, onCloseClick}) {
    const isMounted = useRef(false);
    const {setPageTitle, showMessage, handleApiError} = useAppContext();
    const [isLoading, setIsLoading] = useState(false);
    const [initialValues, setInitialValues] = useState({...DEFAULT_INSTANCE});
    const [parameterOptions, setParameterOptions] = useState([]);
    const isDraft = reportTemplate.status === 1;
    const isEditable = isSystemAdmin();

    useEffect(() => {
        isMounted.current = true;
        setPageTitle(isEmpty(eventTemplate) ? 'New event template' : 'Event template');
        getInitialValues();
        setParameterOptions(Object.entries(reportTemplateParameters).map(([k, v]) => ({label: v, value: k})));
        return () => isMounted.current = false;
    }, []);

    const getInitialValues = () => {
        if (isMounted.current) {
            setInitialValues(oldData => {
                const newData = {};
                for (const [key, value] of Object.entries(oldData)) {
                    newData[key] = eventTemplate.hasOwnProperty(key) ? eventTemplate[key] : value;
                }
                return newData;
            });
        }
    };

    const handleSubmit = (values, {setSubmitting, setErrors}) => {
        setIsLoading(true);
        const payload = {...values};
        const onError = error => handleFailureResponse(error, setErrors);
        const onDone = () => {
            if (isMounted.current) {
                setSubmitting(false);
                setIsLoading(false);
            }
        };
        upsertEventTemplate(reportTemplate.id, maintenanceItemTemplate.id, payload, handleSuccessResponse, onError, onDone);
    };

    const handleSuccessResponse = (response) => {
        showMessage('success', 'Event template has been successfully saved');
        onCloseClick();
    };

    const handleFailureResponse = (error, setErrors) => {
        handleApiError(error);
        if (isMounted.current && error.response) setErrors(error.response.data.errors);
    };

    return (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={VALIDATION_SCHEMA}
          onSubmit={handleSubmit}
        >
            {(props) => (
                <EventTemplateForm
                  {...props}
                  parameterOptions={parameterOptions}
                  isDraft={isDraft}
                  isEditable={isEditable}
                  isLoading={isLoading}
                  onCloseClick={onCloseClick}
                />
            )}
        </Formik>
    );
}
